import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import { connect } from 'react-redux';
// Types
import type { LineItem } from '../types';
// Components
import {
  ItemListImage,
  Button
} from '../../common';
import FormControlInput from "../../common/components/FormControlInput";
// Utils
import validationHelpers from "../../common/validationHelpers";
// Styles
import "./GiftCardItem.css"
// Actions
import { deleteItem, changeGiftCard, dismissGiftCardFieldError } from '../actions';
//Selector
import { editingItemLoadingByIdSelector } from '../selectors';

type Props = {
  item: LineItem,
  error: string,
}

registerLocale('pt-BR', ptBR);

const GiftCardItem = ({item, handleRemoveItem, handleUpdateCard, updatingItemLoading, error, dismissGiftCardFieldError}: Props) => {
  const accountFormInitialState = {
    name: item.giftCard.receiverName,
    message: item.giftCard.message,
    email: item.giftCard.email,
    sendAt: item.giftCard.sendAt,
  }

  const [accountForm, setAccountForm] = useState(accountFormInitialState);
  const [formErrors, setFormErrors] = useState({
    nameError: '',
    emailError: '',
    sendAtError: '',
  });
  const [hasFormChanged, setHasFormChanged] = useState(false)

  const giftCardErrors = error ? error?.[0]?.gift_card?.[0] : null;

  const giftCardErrorsMap = {
    nameError: giftCardErrors?.receiver_name || null,
    emailError: giftCardErrors?.email || null,
    sendAtError: giftCardErrors?.send_at || null,
  };

  const handleFormChange = (target: string, value: string) => {
    setAccountForm(prev => ({ ...prev, [target]: value }));
    if (accountForm[target] !== value) setFormErrors(prev => ({ ...prev, [`${target}Error`]: '' }));

    const serverFieldMap = {
      name: 'receiver_name',
      email: 'email',
      sendAt: 'send_at',
    };

    const serverField = serverFieldMap[target];
    if (serverField) {
      dismissGiftCardFieldError(item.id, serverField);
    }

    setHasFormChanged(true)
  };

  const validateUserInput = () => {
    let noValidationErrors = true;

    const accountFormFieldToErrorMap = {
      name: 'nameError',
      email: 'emailError',
      sendAt: 'sendAtError',
    }

    Object.entries(accountFormFieldToErrorMap).forEach(([field, errorField]) => {
      const validationFunction = validationHelpers.giftCard[field];
      const error = validationFunction(
        accountForm[field]
      );

      setFormErrors(prev => ({ ...prev, [errorField]: error }));
      if (error) noValidationErrors = false;
    })

    return noValidationErrors;
  };

  const handleUpdateGiftCard = () => {
    const noValidationErrors = validateUserInput();
    if (!noValidationErrors) return false;

    handleUpdateCard (
      item.id,
      accountForm.name,
      accountForm.message,
      accountForm.email,
      accountForm.sendAt,
      item.giftCard.amount,
    )

    setHasFormChanged(false)
    return true
  }

  return (
    <div className="item-list-main-container">
      <div className="item-list-image-container">
        <section className="search-result-square-image-section-cart">
          <ItemListImage variant={item.variant} />
        </section>
        {window.innerWidth < 768 && <div className="gift-card-list-header">    
          <div className="line-item-content">
            <p>{item.variant.manufacturer}</p>
            <Link to={`/produtos/${item.variant.slug}/`}>
              <h5 className="line-item-name-text">{item.variant.name}</h5>
            </Link>
          </div>
        </div>}
      </div>
      <div className="gift-card-list-content">
        {window.innerWidth > 768 &&  <div className="gift-card-list-header">
          <div className="line-item-content">
            <p>{item.variant.manufacturer}</p>
            <Link to={`/produtos/${item.variant.slug}/`}>
              <h5 className="line-item-name-text">{item.variant.name}</h5>
            </Link>
          </div>
          <div className="gift-card-price-container">
            <div className="gift-card-price">
              {parseFloat(
                item.giftCard.amount
              ).toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}{' '}
            </div>
            <div className="line-item-price-container-option">
              <button
                className="item-list-trash-button"
                aria-hidden="true"
                onClick={() => handleRemoveItem(item.id)}
              >
                Excluir
              </button>
            </div>
          </div>
        </div>}
        <div className="gift-card-line-item-content">
          <FormControlInput
            variant="rounded"
            label="Email do Destinatário"
            value={accountForm.email}
            handleChangeText={value => handleFormChange('email', value)}
            handleBlur={() => validateUserInput()}
            handleFocus={value => handleFormChange('email', value)}
            errorMessage={giftCardErrorsMap.emailError || formErrors.emailError || ''}
          />
        </div>
        <div className="gift-card-line-item-content">
          <label className="gift-card-message-label">
            Mensagem
          </label>
          <textarea
            className="gift-card-form-control-input-wrapper-message"
            placeholder="Mensagem para o Destinatário"
            value={accountForm.message}
            onChange={e => handleFormChange('message', e.target.value)}
          />
        </div> 
        <div className="gift-card-line-item-content-shared">
          <FormControlInput
            variant="rounded"
            label="Nome do Destinatário"
            value={accountForm.name}
            handleChangeText={value => handleFormChange('name', value)}
            handleBlur={() => validateUserInput()}
            handleFocus={value => handleFormChange('name', value)}
            errorMessage={giftCardErrorsMap.nameError || formErrors.nameError || ''}
          />
          <div className="form-control-input-wrapper">
          <label className="input-with-address-autocomplete-label">
            Data de envio 
          </label>
          <ReactDatePicker
            locale="pt-BR"
            placeholderText="Selecione data e hora"
            className="form-control-input form-control-input-rounded" 
            selected={accountForm.sendAt ? new Date(accountForm.sendAt) : null}
            onChange={(date) => {
              handleFormChange('sendAt', date ? date.toISOString() : '');
            }}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="P p"
          />
          {(giftCardErrorsMap.sendAtError || formErrors.sendAtError) && (
            <span className="form-control-input-message form-control-input-message-animation">
              {giftCardErrorsMap.sendAtError || formErrors.sendAtError}
            </span>
          )}
        </div>
        </div>
        
        <div className="gift-card-button-container">
          <div className="gift-card-save-button">
          <Button
            type="submit"
            text="Editar"
            loading={updatingItemLoading}
            onClick={handleUpdateGiftCard}
            buttonType="button-primary"
            disabled={!hasFormChanged}
          />
          </div>
        </div>
      </div>
    </div>
  )
}


const mapStateToProps = (state, ownProps: OwnProps) => {
  return {
    updatingItemLoading: editingItemLoadingByIdSelector(
      state,
      ownProps.item.id,
    ),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    handleRemoveItem: (lineItemId: number) => {
      dispatch(deleteItem(lineItemId));
    },
    handleUpdateCard: (
      productId: number,
      receiverName: string,
      message: string,
      email: string,
      sendAt: string,
      amount: number,
    ) => {
      dispatch(
        changeGiftCard(
          productId,
          receiverName,
          message,
          email,
          sendAt,
          amount,
        )
      )
    },
    dismissGiftCardFieldError: (productId: number, field: string) => {
      dispatch(dismissGiftCardFieldError(productId, field));
    },
  };
};

const connector: Connector<{}, Props> = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default connector(GiftCardItem)